import { useState } from "react";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { MapPin, Phone, Mail, Clock, Check, Loader2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useFirebase } from "@/contexts/FirebaseContext";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const Contact = () => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { getFirestore } = useFirebase();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    enquiryType: "",
    message: "",
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSelectChange = (value: string) => {
    setFormData((prevData) => ({
      ...prevData,
      enquiryType: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const firestore = getFirestore();
      const docRef = await addDoc(collection(firestore, "contacts"), {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        enquiryType: formData.enquiryType,
        message: formData.message,
        timestamp: serverTimestamp(),
      });
      toast({
        title: "Message sent successfully!",
        description: "We'll get back to you shortly.",
      });
      setFormSubmitted(true);
      setIsSubmitting(false);
      setTimeout(() => {
        setFormSubmitted(false);
        setFormData({
          name: "",
          email: "",
          phone: "",
          enquiryType: "",
          message: "",
        });
      }, 5000);
    } catch (error) {
      toast({
        title: "Error",
        description: "There was an error sending your message. Please try again later.",
      });
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-drivewise-900 to-drivewise-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Contact Us</h1>
          <p className="text-xl max-w-3xl mx-auto">
            Have questions or ready to start your driving journey? Get in touch with our friendly team.
          </p>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg text-center flex flex-col items-center">
              <div className="bg-drivewise-100 p-4 rounded-full mb-6">
                <MapPin className="h-8 w-8 text-drivewise-600" />
              </div>
              <h3 className="text-xl font-bold text-road-900 mb-3">Postal Location</h3>
              <p className="text-road-700">
                167 - 169 Great Portland Street<br />
                5th Floor<br />
                London<br />
                W1W 5PF<br />
                United Kingdom
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg text-center flex flex-col items-center">
              <div className="bg-drivewise-100 p-4 rounded-full mb-6">
                <Phone className="h-8 w-8 text-drivewise-600" />
              </div>
              <h3 className="text-xl font-bold text-road-900 mb-3">Call Us</h3>
              <p className="text-road-700 mb-2">
                +442034889598
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg text-center flex flex-col items-center">
              <div className="bg-drivewise-100 p-4 rounded-full mb-6">
                <Mail className="h-8 w-8 text-drivewise-600" />
              </div>
              <h3 className="text-xl font-bold text-road-900 mb-3">Email Us</h3>
              <p className="text-road-700 mb-2">
                info@way2learn.co.uk
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg text-center flex flex-col items-center">
              <div className="bg-drivewise-100 p-4 rounded-full mb-6">
                <Clock className="h-8 w-8 text-drivewise-600" />
              </div>
              <h3 className="text-xl font-bold text-road-900 mb-3">Opening Hours</h3>
              <p className="text-road-700 mb-2">
                Monday-Friday: 8am - 8pm
              </p>
              <p className="text-road-700">
                Saturday-Sunday: 9am - 5pm
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-road-900 mb-6">Send Us a Message</h2>
              <p className="text-lg text-road-700 mb-8">
                Fill out the form below and our team will get back to you as soon as possible.
              </p>

              {formSubmitted ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                  <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <Check className="h-6 w-6 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-green-800 mb-2">Thank You!</h3>
                  <p className="text-green-700">
                    Your message has been sent successfully. We'll get back to you shortly.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="John Smith"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="john@example.com"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="(01) 234 5678"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="enquiryType">Enquiry Type</Label>
                      <Select
                        value={formData.enquiryType}
                        onValueChange={handleSelectChange}
                      >
                        <SelectTrigger id="enquiryType">
                          <SelectValue placeholder="Select an option" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General Enquiry</SelectItem>
                          <SelectItem value="booking">Booking Information</SelectItem>
                          <SelectItem value="pricing">Pricing & Packages</SelectItem>
                          <SelectItem value="instructor">Instructor Enquiry</SelectItem>
                          <SelectItem value="complaint">Complaint</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="message">Message</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="How can we help you?"
                      rows={5}
                      required
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    size="lg"
                    className="bg-drivewise-600 hover:bg-drivewise-700 text-white"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? <Loader2 className="animate-spin h-4 w-4 mr-2" /> : null}
                    Send Message
                  </Button>
                </form>
              )}
            </div>
            
            {/* Map */}
            <div>
              <h2 className="text-3xl font-bold text-road-900 mb-6">Find Us</h2>
              <p className="text-lg text-road-700 mb-8">
                Visit our main office or one of our training centers located throughout the city.
              </p>
              <div className="aspect-[4/3] w-full bg-gray-200 rounded-lg overflow-hidden relative">
                {/* In a real application, you would integrate Google Maps or another mapping service here */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <p className="text-road-600 text-center px-4">
                    Interactive map would be displayed here.<br />
                    In a real application, this would be integrated with Google Maps or another mapping service.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-road-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-road-600 max-w-3xl mx-auto">
              Find quick answers to common questions about our driving school.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-road-900 mb-3">How do I book a driving lesson?</h3>
              <p className="text-road-700">
                You can book lessons through our online booking system, by calling our booking line, or by contacting your assigned instructor directly if you're an existing student.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-road-900 mb-3">What should I bring to my first lesson?</h3>
              <p className="text-road-700">
                Please bring your provisional driving license, comfortable shoes suitable for driving, and any glasses or contact lenses if you require them for driving.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-road-900 mb-3">How do I pay for lessons?</h3>
              <p className="text-road-700">
                We accept payment by credit/debit card, bank transfer, or cash. You can pay per lesson or take advantage of our discounted block booking packages.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-road-900 mb-3">What's your cancellation policy?</h3>
              <p className="text-road-700">
                We require 48 hours' notice for cancellations. Cancellations with less notice may be charged the full lesson fee, subject to instructor discretion.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-road-900 mb-3">Do you offer intensive driving courses?</h3>
              <p className="text-road-700">
                Yes, we offer a range of intensive courses from one-week to three-week programs, depending on your experience level and availability.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-road-900 mb-3">Can I choose my instructor?</h3>
              <p className="text-road-700">
                Yes, you can request a specific instructor when booking. We also do our best to match students with instructors whose teaching style will suit them best.
              </p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;
